import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";

import { PageHeader, LabelM, SubtitleM } from "../../components/Typography";
import useApiCall from "../../hooks/useApiCall";
import AgentBreadcrumbs from "../../components/AgentBreadcrumbs";

const LABELM_STYLE_EXT = { fontSize: "1rem", textAlign: "left", marginBottom: "8px" };

export default function AgentDeploymentsPage() {
  const { id } = useParams();
  const tokens = useSelector((state) => state?.tokens.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();

  const [agent, setAgent] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    business_unit_id: "",
    channel_id: "",
    environment: "staging",
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [agentResp, deploymentsResp, businessUnitsResp, channelsResp] = await Promise.all([
        makeRequest(`/v2/agent_instances/${id}`, {
          method: 'GET',
          authTokens: tokens,
        }),
        makeRequest(`/v2/agents/${id}/deployments`, {
          method: 'GET',
          authTokens: tokens,
        }),
        makeRequest(`/v2/business_units?app_id=${currentApp?.id}`, {
          method: 'GET',
          authTokens: tokens,
        }),
        makeRequest('/v2/channels', {
          method: 'GET',
          authTokens: tokens,
        }),
      ]);

      setAgent(agentResp.agent || agentResp);
      setDeployments(deploymentsResp.deployments || []);
      setBusinessUnits(businessUnitsResp.business_units || []);
      setChannels(channelsResp.channels || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      alert(`Failed to load data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      business_unit_id: "",
      channel_id: "",
      environment: "staging",
    });
  };

  const handleCreateDeployment = async () => {
    if (!formData.business_unit_id || !formData.channel_id) {
      alert("Business Unit and Channel are required");
      return;
    }

    try {
      await makeRequest(`/v2/agents/${id}/deployments`, {
        method: 'POST',
        body: {
          deployment: {
            business_unit_id: parseInt(formData.business_unit_id),
            channel_id: parseInt(formData.channel_id),
            environment: formData.environment,
            status: 'active',
          },
        },
        authTokens: tokens,
      });
      alert("Deployment created successfully");
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error("Error creating deployment:", error);
      alert(
        `Failed to create deployment: ${
          error.response?.data?.error || error.message
        }`
      );
    }
  };

  const handleToggleStatus = async (deployment) => {
    const action = deployment.status === 'active' ? 'deactivate' : 'activate';

    try {
      await makeRequest(`/v2/agents/${id}/deployments/${deployment.id}/${action}`, {
        method: 'POST',
        authTokens: tokens,
      });
      alert(`Deployment ${action}d successfully`);
      fetchData();
    } catch (error) {
      console.error(`Error ${action}ing deployment:`, error);
      alert(`Failed to ${action} deployment: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <AgentBreadcrumbs agentId={id} />
      <PageHeader>
        Deployments: {agent?.name}
      </PageHeader>
      <SubtitleM>
        Manage where this agent is deployed (Business Units × Channels × Environments)
      </SubtitleM>

      {/* Action Bar */}
      <Box sx={{ display: "flex", gap: 2, my: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Create Deployment
        </Button>
      </Box>

      {/* Deployments Table */}
      {deployments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <SubtitleM>No deployments configured yet</SubtitleM>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{ mt: 2 }}
          >
            Create Your First Deployment
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Business Unit</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Deployed By</TableCell>
                <TableCell>Deployed At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deployments.map((deployment) => (
                <TableRow key={deployment.id} hover>
                  <TableCell>
                    <strong>{deployment.business_unit?.name}</strong>
                    <br />
                    <small style={{ color: '#666' }}>
                      {deployment.business_unit?.unit_code}
                    </small>
                  </TableCell>
                  <TableCell>
                    {deployment.channel?.name}
                    <br />
                    <Chip
                      label={deployment.channel?.channel_type}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={deployment.environment}
                      color={deployment.environment === 'production' ? 'error' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={deployment.status}
                      color={deployment.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{deployment.deployed_by || '-'}</TableCell>
                  <TableCell>
                    {deployment.deployed_at
                      ? new Date(deployment.deployed_at).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleToggleStatus(deployment)}
                      color={deployment.status === 'active' ? 'warning' : 'success'}
                    >
                      {deployment.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Deployment Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Deployment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <Box>
              <LabelM style={LABELM_STYLE_EXT}>Business Unit *</LabelM>
              <TextField
                select
                fullWidth
                value={formData.business_unit_id}
                onChange={(e) =>
                  setFormData({ ...formData, business_unit_id: e.target.value })
                }
              >
                <MenuItem value="">Select Business Unit</MenuItem>
                {businessUnits.map((bu) => (
                  <MenuItem key={bu.id} value={bu.id}>
                    {bu.name} ({bu.unit_code})
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box>
              <LabelM style={LABELM_STYLE_EXT}>Channel *</LabelM>
              <TextField
                select
                fullWidth
                value={formData.channel_id}
                onChange={(e) =>
                  setFormData({ ...formData, channel_id: e.target.value })
                }
              >
                <MenuItem value="">Select Channel</MenuItem>
                {channels.map((channel) => (
                  <MenuItem key={channel.id} value={channel.id}>
                    {channel.name} ({channel.channel_type})
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box>
              <LabelM style={LABELM_STYLE_EXT}>Environment *</LabelM>
              <TextField
                select
                fullWidth
                value={formData.environment}
                onChange={(e) =>
                  setFormData({ ...formData, environment: e.target.value })
                }
              >
                <MenuItem value="staging">Staging</MenuItem>
                <MenuItem value="production">Production</MenuItem>
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleCreateDeployment} variant="contained">
            Create Deployment
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
